import { registerLocaleData } from '@angular/common';
import { inject, Injectable, signal, Signal } from '@angular/core';
import { defineLocale } from 'ngx-bootstrap/chronos';
import { BsDatepickerConfig, BsDaterangepickerConfig, BsLocaleService } from 'ngx-bootstrap/datepicker';
import { esLocale, ptBrLocale } from 'ngx-bootstrap/locale';
import { STORAGE_TYPE, StorageService } from '../storage';
import { LANG_EN_CONFIG } from './config/en';
import { LANG_ES_CONFIG } from './config/es';
import { LANG_PT_CONFIG } from './config/pt';
import { LANG_EN_DICTIONARY } from './dictionary/en';
import { LANG_ES_DICTIONARY } from './dictionary/es';
import { LANG_PT_DICTIONARY } from './dictionary/pt';
import { Config, Languages } from './translate.types';

defineLocale('es', esLocale);
defineLocale('pt-br', ptBrLocale);

/**
 * Serviço de i18n manual com reatividade via signals.
 * `currentLanguage` é a fonte de verdade reativa — pipes e componentes
 * leem este signal para invalidar traduções sem `pure: false`.
 */
@Injectable({ providedIn: 'root' })
export class TranslateService {
  private readonly bsLocaleService = inject(BsLocaleService);
  private readonly bsDatepickerConfig = inject(BsDatepickerConfig);
  private readonly bsDaterangePickerConfig = inject(BsDaterangepickerConfig);
  private readonly storageService = inject(StorageService);

  private readonly languageSignal = signal<Languages>('pt');
  private readonly config: Map<string, Config> = new Map<string, Config>();
  private readonly dictionary: Map<string, Map<string, unknown>> = new Map<string, Map<string, unknown>>();

  /** Signal reativo do idioma atual — consumido por TranslatePipe e componentes zoneless. */
  public readonly currentLanguage: Signal<Languages> = this.languageSignal.asReadonly();

  constructor() {
    this.loadConfig();
    this.loadDictionary();
    const language: Languages = this.loadLanguage();
    this.languageSignal.set(language);
    this.setLocaleDate();
    this.saveLanguage();
  }

  public useLanguage = (language: Languages): void => {
    this.languageSignal.set(language);
    this.setLocaleDate();
    this.saveLanguage();
  };

  public translate = (key: string): string => {
    const dictionary: Map<string, unknown> | undefined = this.dictionary.get(this.languageSignal());
    const value: string = (dictionary?.get(key) as string) ?? key;
    return value;
  };

  public get getConfig(): Config {
    const config: Config | undefined = this.config.get(this.languageSignal());
    return config;
  }

  private readonly setLocaleDate = (): void => {
    registerLocaleData(this.getConfig.localeDate);
    this.bsLocaleService.use(this.getConfig.localeDate);
    this.bsDatepickerConfig.dateInputFormat = this.getConfig.date.format;
    this.bsDatepickerConfig.showWeekNumbers = false;
    this.bsDatepickerConfig.containerClass = 'ds-datepicker';
    this.bsDatepickerConfig.isAnimated = true;
    this.bsDaterangePickerConfig.dateInputFormat = this.getConfig.date.format;
    this.bsDaterangePickerConfig.showWeekNumbers = false;
    this.bsDaterangePickerConfig.containerClass = 'ds-datepicker';
    this.bsDaterangePickerConfig.isAnimated = true;
  };

  private readonly loadConfig = (): void => {
    this.config.set('en', LANG_EN_CONFIG);
    this.config.set('es', LANG_ES_CONFIG);
    this.config.set('pt', LANG_PT_CONFIG);
  };

  private readonly loadDictionary = (): void => {
    this.dictionary.set('en', this.loadDictionaryTranslates(LANG_EN_DICTIONARY));
    this.dictionary.set('es', this.loadDictionaryTranslates(LANG_ES_DICTIONARY));
    this.dictionary.set('pt', this.loadDictionaryTranslates(LANG_PT_DICTIONARY));
  };

  private readonly loadDictionaryTranslates = (language: unknown): Map<string, unknown> => {
    const dictionary: Map<string, unknown> = new Map<string, unknown>();
    Object.entries(language as Record<string, unknown>).forEach(([key, value]) => {
      dictionary.set(key, value);
    });
    return dictionary;
  };

  private readonly loadLanguage = (): Languages => {
    const language: Languages = this.storageService.get<Languages>(STORAGE_TYPE.LOCAL, 'language') ?? 'pt';
    return language;
  };

  private readonly saveLanguage = (): void => {
    this.storageService.set(STORAGE_TYPE.LOCAL, 'language', this.languageSignal());
  };
}
